import styled, { css } from 'styled-components';
import { BaseSyntheticEvent, useEffect, useState, useContext } from 'react';
import { BlueLink } from 'components/common/link';
import { openModal } from 'components/common/modal/modal-actions';
import { TranslationContext } from 'contexts/translation-context';
import { formatFloatWithCommasAndDecimals } from 'utils/new-formats';
import { Tooltip } from 'components/common/tooltip';
import { Icon } from 'components/common/icon/icon';
import { useDispatch, useSelector } from 'react-redux';
import {
  allAvailableAddons,
  allAvailablePlans,
  getAddonName,
  getAddonText,
  getPlanColor,
  getPlanText,
  planNameToPackageId,
  foundValue,
  onlyYearlyPlans,
  splitPlanName
} from 'utils/package-helper';
import type { JoinFormType, PackagePrices } from 'types/join';
import { FormikProps } from 'formik';
import { countries } from 'constants/locations';
import { PROMOTIONAL_OFFERS, PromotionalOffer } from 'constants/plan';
import { getPackagePrices } from '../actions';
import { AdditionalPlans } from './additional-plans';
import { PlanState } from '../reducers/plan';
import { getPlanName } from './formats';
import { AddonsSection } from './addons-section';

const Container = styled.div`
  margin-bottom: 0;
  margin-top: 0;
  font-family: ${props => props.theme.fonts.interSans};
`;
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;
const NumberCircle = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.colors.siteIndigo};
  color: ${props => props.theme.colors.siteIndigo};
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-top: 1px;
  box-sizing: border-box;
`;
const SectionTitle = styled.div`
  font-family: ${props => props.theme.fonts.FunnelDisplay};
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${props => props.theme.colors.black};
`;
const ButtonsContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  background-color: ${props => props.theme.colors.white};
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 14px;
  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;
const BilledAnnually = styled.button<{ active?: boolean }>`
  flex: 1;
  height: 32px;
  text-align: center;
  color: ${props => (props.active ? props.theme.colors.white : props.theme.colors.siteBlack)};
  font-size: 12px;
  background-color: ${props => (props.active ? props.theme.colors.siteIndigo : 'transparent')};
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.interSansMedium};
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
`;
const BilledMonthly = styled(BilledAnnually)``;
const YourPlanBox = styled.div<{ disabled?: boolean }>`
  display: flex;
  min-height: 96px;
  border: 1px solid ${props => props.theme.colors.siteCardBorder};
  border-radius: 10px;
  background-color: ${props => props.theme.colors.white};
  padding: 14px;
  flex-direction: column;
  overflow: hidden;
  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;
const TitleWrapper = styled.div`
  position: relative;
`;
const VerticalWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const HorizontalWrapper = styled.div<{ alignEnd?: boolean; marginTop?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  ${props => props.alignEnd && `align-items: flex-end;`}
  ${props => props.marginTop && `margin-top: ${props.marginTop};`}
`;

const Text = styled.div<{
  color?: string;
  bold?: boolean;
  regular?: boolean;
  size?: number;
  textAlign?: string;
  paddingTop?: string;
  padding?: string;
  cursor?: string;
}>`
  color: ${props => props.color ?? props.theme.colors.siteBlack};
  font-size: ${props => props.size ?? 11}px;
  text-align: ${props => props.textAlign ?? 'unset'};
  font-family: ${props => {
    if (props.bold) {
      return props.theme.fonts.FunnelDisplay;
    }

    if (props.regular) {
      return props.theme.fonts.interSans;
    }
    return props.theme.fonts.interSansMedium;
  }};
  line-height: 1.35;
  ${props => props.paddingTop && `padding-top: ${props.paddingTop}`};
  ${props => props.padding && `padding: ${props.padding}`};
  ${props => props.cursor && `cursor: ${props.cursor}`};
`;

const Line = styled.div`
  margin: 10px 0;
  border-bottom: 1px solid ${props => props.theme.colors.lightGrey};
`;
const OtherPlansLink = styled(BlueLink)`
  font-size: 12px;
  padding-top: 20px;
  padding-bottom: 40px;
  font-family: ${props => props.theme.fonts.interSans};
  display: block;
  text-align: center;
  color: ${props => props.disabled && props.theme.colors.toggleSwitchGrey};
  :hover {
    color: ${props => props.disabled && props.theme.colors.toggleSwitchGrey};
  }
`;
const StyledEditLink = styled(BlueLink)`
  font-size: 11px;
  font-family: ${props => props.theme.fonts.interSansMedium};
  position: absolute;
  right: 0;
  top: 0;
`;

const OtherPlansLinkTooltipIcon = styled(Icon)`
  color: ${props => props.theme.colors.toggleSwitchGrey};
  font-size: 11px;
  padding-left: 3px;
  margin-right: 7px;
`;

const SaveTag = styled.div`
  background-color: #fbf5e8;
  color: #896717;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  margin-left: 8px;
  align-self: flex-end;
  margin-bottom: 2px;
`;

const OriginalPrice = styled.div<{ textAlign?: string }>`
  color: #cccccc;
  text-decoration: line-through;
  font-size: 12px;
  font-family: ${props => props.theme.fonts.interSans};
  text-align: ${props => props.textAlign ?? 'unset'};
  margin-bottom: 4px;
`;

const OfferText = styled.div<{ textAlign?: string }>`
  color: #434343;
  font-size: 12px;
  font-family: ${props => props.theme.fonts.interSans};
  margin-top: 4px;
  text-align: ${props => props.textAlign ?? 'unset'};
`;

const PlanTitle = styled.div<{ isOnlyYearlyPlans?: boolean }>`
  display: none;
  ${props =>
    props.isOnlyYearlyPlans &&
    css`
      margin-bottom: 13px;
    `}
`;

const YearlyPlanMessage = styled.div`
  text-align: center;
  font-size: 12px;
  font-family: ${props => props.theme.fonts.interSansMedium};
  color: ${props => props.theme.colors.lightGray};
  margin-top: 5px;
  margin-bottom: 10px;
`;

export const YourPlan = ({
  setSelectedAddons,
  savedSelectedAddons,
  selectedPlan,
  setSelectedPlan,
  selectedBilledOption,
  setSelctedBilledOption,
  isActive,
  setIsActive,
  setNumConfirmClicked,
  form,
  isPriceList,
  setDrawerOpen,
  hideAnnuallyYearlyButtons = false
}: {
  setSelectedAddons: (data: (PackagePrices | undefined)[]) => void;
  savedSelectedAddons?: (PackagePrices | undefined)[];
  selectedPlan: PackagePrices | undefined;
  setSelectedPlan: (data: PackagePrices | undefined) => void;
  selectedBilledOption: string;
  setSelctedBilledOption: (data: string) => void;
  isActive: boolean;
  setIsActive: (data: boolean) => void;
  setNumConfirmClicked: (data: number) => void;
  form: FormikProps<JoinFormType>;
  isPriceList: boolean;
  setDrawerOpen?: (open: boolean) => void;
  isDrawerOpen?: boolean;
  hideAnnuallyYearlyButtons?: boolean;
}) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const dispatch = useDispatch();
  const [selectedAddons, setSelectedAddon] = useState<Array<PackagePrices>>(
    () => savedSelectedAddons?.filter((addon): addon is PackagePrices => Boolean(addon)) ?? []
  );
  const [isUsaAccount, setIsUsaAccount] = useState(false);
  const [numOfAdditionalUser, setNumOfAdditionalUser] = useState('1');
  const [errorAdditionalUser, setErrorAdditionalUser] = useState(false);
  const companyCountry = form.values.companyCountry;

  const [userAvailablePlans, setUserAvailablePlans] = useState<Array<PackagePrices>>([]);
  const [planText, setPlanText] = useState('');
  const [availableAddons, setAvailableAddons] = useState<Array<PackagePrices | undefined>>([]);
  const { packagePrices, currentContract } = useSelector(
    (state: { plan: PlanState }) => state.plan
  ) || {
    packagePrices: null,
    currentContract: null
  };

  useEffect(() => {
    dispatch(getPackagePrices());
  }, []);

  useEffect(() => {
    if (currentContract !== null && currentContract.draftMainPackageName !== null) {
      setPlanText(currentContract.draftMainPackageName);
    } else if (
      form.values.initialPlanPeriod !== null &&
      form.values.initialPlan !== null &&
      (currentContract === null || currentContract.draftMainPackageName === null)
    ) {
      setPlanText(form.values.initialPlan);
      setSelctedBilledOption(form.values.initialPlanPeriod);
      if (form.values.initialPlan?.toLowerCase().includes('india')) {
        form.setFieldValue('companyCountry', 'India');
      }
    }
  }, [currentContract, form.values.initialPlan]);

  useEffect(() => {
    const planPeriod = form.values.initialPlanPeriod;

    if (planPeriod === 'monthly' || planPeriod === '1month' || planPeriod === '1 month') {
      setSelctedBilledOption(TRANSLATION_TEXT.billed_monthly);
    } else if (
      planPeriod === 'annually' ||
      planPeriod === '1 year' ||
      planPeriod === '1year' ||
      planPeriod === 'yearly'
    ) {
      setSelctedBilledOption(TRANSLATION_TEXT.billed_annually);
    }
  }, [TRANSLATION_TEXT, setSelctedBilledOption]);

  useEffect(() => {
    setSelectedAddons(selectedAddons ?? []);
  }, [selectedAddons]);

  useEffect(() => {
    setIsUsaAccount(
      form.values.companyCountry === 'USA' || form.values.companyCountry === 'United States'
    );
  }, [form.values.companyCountry]);

  const findAndUnselectAddon = (packageId: string) => {
    const filteredArray = selectedAddons?.filter(p => p?.packageId !== packageId);
    if (filteredArray === undefined) return;
    setSelectedAddon(filteredArray);
  };

  const isAddonSelected = (packageId: string) => {
    return selectedAddons.filter(addon => addon?.packageId === packageId).length > 0;
  };
  const getPackage = (packageId: string, mainPackageId: string, numOfMemberAddons?: string) => {
    let internalId = 0;
    let mainPackageInternalId = 0;
    const curDate = new Date();
    let period = '';
    if (
      selectedBilledOption === TRANSLATION_TEXT.billed_annually ||
      selectedBilledOption === TRANSLATION_TEXT.one_year
    ) {
      period = '1 year';
    } else if (selectedBilledOption === TRANSLATION_TEXT.billed_monthly) {
      period = 'monthly';
    }

    if (packageId === null || packageId === '') return undefined;
    let numberOfMemberAddons = numOfAdditionalUser === '' ? '1' : numOfAdditionalUser;
    if (numOfMemberAddons) {
      numberOfMemberAddons = numOfMemberAddons;
    }
    packagePrices?.forEach(p => {
      if (
        mainPackageInternalId === 0 &&
        p.mainPackageMode &&
        p.packageId === mainPackageId &&
        p.period.toLowerCase() === period &&
        (p.country?.toLowerCase() === 'all' ||
          (isUsaAccount && p.country?.toLowerCase() === 'usa') ||
          (!isUsaAccount && p.country?.toLowerCase() === 'non-usa') ||
          (companyCountry?.toLowerCase() === 'india' && p.country?.toLowerCase() === 'india'))
      ) {
        mainPackageInternalId = p.internalId;
      }
    });

    packagePrices?.forEach(p => {
      if (
        p.packageId === packageId &&
        p.period.toLowerCase() === period &&
        (p.country?.toLowerCase() === 'all' ||
          (isUsaAccount && p.country?.toLowerCase() === 'usa') ||
          (!isUsaAccount && p.country?.toLowerCase() === 'non-usa') ||
          (companyCountry?.toLowerCase() === 'india' && p.country?.toLowerCase() === 'india')) &&
        (p.isMemberAddon === false ||
          (p.additionalMembersMax !== null &&
            p.additionalMembersMin !== null &&
            p.additionalMembersMin <= Number(numberOfMemberAddons) &&
            (!(p.additionalMembersMax > 0) ||
              Number(numberOfMemberAddons) <= p.additionalMembersMax)))
      ) {
        const endDate = new Date(p.endDate);
        const startDate2 = new Date(p.startDate);
        if (startDate2 <= curDate && (p.endDate === null || endDate >= curDate)) {
          if (
            foundValue(p.listInternalIds, mainPackageInternalId) ||
            mainPackageInternalId === p.internalId
          ) {
            internalId = p.internalId;
          }
        }
      }
    });
    return packagePrices?.filter(prices => prices.internalId === internalId)[0];
  };

  useEffect(() => {
    setSelectedPlan(getPackage(selectedPlan?.packageId ?? '', selectedPlan?.packageId ?? ''));
  }, [selectedBilledOption]);

  // Auto-set to yearly billing for Gem plans and India plans
  useEffect(() => {
    const packageName = selectedPlan?.packageName || '';
    if (
      packageName.includes('Gem Dealer') ||
      packageName.includes('Gem Buyer') ||
      packageName.includes('Buyer India') ||
      packageName.includes('Dealer Lite India')
    ) {
      if (selectedBilledOption === TRANSLATION_TEXT.billed_monthly) {
        setSelctedBilledOption(TRANSLATION_TEXT.billed_annually);
      }
    }
  }, [
    selectedPlan?.packageName,
    selectedBilledOption,
    TRANSLATION_TEXT.billed_annually,
    TRANSLATION_TEXT.billed_monthly
  ]);

  useEffect(() => {
    let packages = [
      ...new Set(
        packagePrices?.filter(
          p =>
            allAvailableAddons.includes(p.packageName) &&
            p.listInternalIds.includes(selectedPlan?.internalId ?? -1) &&
            p.period === selectedPlan?.period
        )
      )
    ];
    packages = packages.reduce((acc: PackagePrices[], current: PackagePrices) => {
      if (!acc.some(item => item.packageId === current.packageId)) {
        acc.push(current);
      }
      return acc;
    }, []);

    const packages2 = packages
      ?.map(p => {
        const pack = getPackage(p.packageId, selectedPlan?.packageId ?? '');
        if (pack && pack.packageName === 'Member Addon') {
          pack.addonCount = Number(numOfAdditionalUser);
        }
        return pack;
      })
      .filter(p => {
        if (!p) return false;

        // Handle Rapaport Magazine filtering
        if (
          p.packageName === 'Rapaport Magazine' &&
          isUsaAccount &&
          !selectedPlan?.packageName.includes('Primary Supplier')
        ) {
          return false;
        }

        // Handle Gem plans special addon filtering
        const isGemPlan =
          selectedPlan?.packageName?.includes('Gem Dealer') ||
          selectedPlan?.packageName?.includes('Gem Buyer');
        if (isGemPlan) {
          return (
            p.packageName === 'Rapaport Price List Plus' ||
            p.packageName === 'Rapaport Price List' ||
            p.packageName === 'Member Addon' ||
            p.packageName === 'Rapaport Magazine'
          );
        }

        return true;
      })
      .sort((p1, p2) => ((p1?.price || 0) > (p2?.price || 0) ? 1 : -1));
    setAvailableAddons(packages2);
  }, [selectedPlan, numOfAdditionalUser, isUsaAccount]);

  useEffect(() => {
    const newAddons: Array<PackagePrices> = [];
    selectedAddons
      .filter(
        a => a.packageName !== 'Member Addon' || (a.packageName === 'Member Addon' && !isUsaAccount)
      )
      .forEach(addon => {
        const add = getPackage(addon.packageId, selectedPlan?.packageId || '');
        if (add) {
          add.addonCount = addon.addonCount;
          if (add.packageName === 'Member Addon') {
            add.addonCount = Number(numOfAdditionalUser);
          }
          newAddons.push(add);
        }
      });
    if (
      selectedPlan?.listComplementaryInternalIds &&
      selectedPlan?.listComplementaryInternalIds.length > 0
    ) {
      selectedPlan?.listComplementaryInternalIds.forEach(p => {
        const res = packagePrices?.filter(pr => pr.internalId === p);
        if (
          res?.length &&
          res.length <= 1 &&
          selectedAddons.filter(add => add.packageId === res[0].packageId).length < 2
        ) {
          res[0].addonCount = 1;
          newAddons.push(res[0]);
        }
      });
    } else {
      const filteredAddons = newAddons.filter(add => add.packageName !== 'Complementary Member');
      setSelectedAddon(filteredAddons);
      return;
    }
    setSelectedAddon(newAddons);
  }, [selectedPlan, isUsaAccount]);

  const findAndSelectAddon = (packageId: string) => {
    const memberAddonCount = Math.max(Number(numOfAdditionalUser) || 1, 1);
    const p1 = getPackage(
      packagePrices?.filter(p => p.packageId === (packageId ?? ''))[0].packageId ?? '',
      selectedPlan?.packageId ?? '',
      memberAddonCount.toString()
    );
    if (p1 === undefined) return;
    if (p1.packageName === 'Member Addon' && memberAddonCount > 50) {
      return;
    }
    if (p1.packageName === 'Member Addon') {
      setNumOfAdditionalUser(memberAddonCount.toString());
    }
    p1.addonCount = p1.packageName === 'Member Addon' ? memberAddonCount : 1;
    setSelectedAddon(prev => [...prev, p1]);
  };

  const setNumber = (e: BaseSyntheticEvent, addonPackage: PackagePrices) => {
    if (e.currentTarget.value === '' || e.currentTarget.value === '0') {
      setNumOfAdditionalUser('');
      findAndUnselectAddon(addonPackage.packageId);
    } else if (!isNaN(parseFloat(e.currentTarget.value))) {
      const value = parseFloat(e.currentTarget.value);
      setErrorAdditionalUser(value > 50);
      setNumOfAdditionalUser(value.toString());
      if (value > 50) {
        findAndUnselectAddon(addonPackage.packageId);
      } else {
        const addon = getPackage(
          addonPackage?.packageId ?? '',
          selectedPlan?.packageId ?? '',
          value.toString()
        );
        const memberAddon = selectedAddons.find(add => add?.packageId === addonPackage.packageId);
        if (addon) {
          addon.addonCount = value;
          if (memberAddon) {
            setSelectedAddon([
              ...selectedAddons.filter(add => add?.packageId !== memberAddon.packageId),
              addon
            ]);
          }
        }
      }
    } else {
      setNumOfAdditionalUser('');
      setErrorAdditionalUser(false);
    }
  };

  const handleOtherAvailablePlansSelected = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 999 && setDrawerOpen) {
      setDrawerOpen(false);
    }
    dispatch(
      openModal({
        modalClassName: 'additionalPlansModal',
        component: (
          <AdditionalPlans
            userAvailablePlans={userAvailablePlans ?? []}
            setSelectedPlan={plan => {
              setSelectedPlan(plan);
              if (typeof window !== 'undefined' && window.innerWidth <= 999 && setDrawerOpen) {
                setTimeout(() => setDrawerOpen(true), 300);
              }
            }}
            setSelectedAddon={setSelectedAddon}
            getPlanColor={getPlanColor}
            setNumOfAdditionalUser={setNumOfAdditionalUser}
          />
        )
      })
    );
  };

  useEffect(() => {
    let period = '';
    if (
      selectedBilledOption === TRANSLATION_TEXT.billed_annually ||
      selectedBilledOption === TRANSLATION_TEXT.one_year
    ) {
      period = '1 year';
    } else if (selectedBilledOption === TRANSLATION_TEXT.billed_monthly) {
      period = 'monthly';
    }

    const plans = packagePrices?.filter(
      p =>
        p.mainPackageMode &&
        p.period.toLowerCase() === period &&
        !p.isAddon &&
        p.packageId !== selectedPlan?.packageId &&
        allAvailablePlans.includes(p.packageName) &&
        ((companyCountry?.toLowerCase() === 'india' && p.packageName.includes('India')) ||
          !p.packageName.includes('India'))
    );

    setUserAvailablePlans(plans?.sort((a, b) => (a.hierarchy < b.hierarchy ? 1 : -1)) ?? []);
  }, [packagePrices, selectedPlan, companyCountry]);

  useEffect(() => {
    if ((!selectedPlan || !selectedPlan.packageId) && planText) {
      const pkgId = planNameToPackageId(planText, companyCountry ?? '', packagePrices);
      setSelectedPlan(getPackage(pkgId, pkgId));
    }
  }, [packagePrices, companyCountry, planText]);

  const getYouSaveAmount = (packageName: string) => {
    return (
      (packagePrices?.filter(
        prices => prices.packageName === packageName && prices.period === TRANSLATION_TEXT.monthly
      )[0]?.price || 0) * 12
    );
  };

  const getPromotionalOffer = (packageName: string): PromotionalOffer | null => {
    return PROMOTIONAL_OFFERS[packageName] || null;
  };

  return (
    <Container>
      {!hideAnnuallyYearlyButtons && (
        <SectionHeader>
          <NumberCircle>1</NumberCircle>
          <SectionTitle>{TRANSLATION_TEXT.your_plan}</SectionTitle>
        </SectionHeader>
      )}
      <TitleWrapper>
        <PlanTitle isOnlyYearlyPlans={onlyYearlyPlans(selectedPlan?.packageName ?? '')}>
          {TRANSLATION_TEXT.your_plan}
        </PlanTitle>{' '}
        {!isActive && (
          <StyledEditLink
            onClick={() => {
              setIsActive(true);
              setNumConfirmClicked(0);
            }}
          >
            {TRANSLATION_TEXT.edit_plan}
          </StyledEditLink>
        )}
      </TitleWrapper>
      {onlyYearlyPlans(selectedPlan?.packageName ?? '') && (
        <YearlyPlanMessage>{TRANSLATION_TEXT.yearly_plan_message}</YearlyPlanMessage>
      )}
      {!onlyYearlyPlans(selectedPlan?.packageName ?? '') && !hideAnnuallyYearlyButtons && (
        <ButtonsContainer disabled={!isActive}>
          <BilledAnnually
            active={
              selectedBilledOption === TRANSLATION_TEXT.billed_annually ||
              selectedBilledOption === TRANSLATION_TEXT.one_year
            }
            onClick={() => setSelctedBilledOption(TRANSLATION_TEXT.billed_annually)}
          >
            {TRANSLATION_TEXT.billed_annually}
          </BilledAnnually>
          <BilledMonthly
            active={selectedBilledOption === TRANSLATION_TEXT.billed_monthly}
            onClick={() => setSelctedBilledOption(TRANSLATION_TEXT.billed_monthly)}
          >
            {TRANSLATION_TEXT.billed_monthly}
          </BilledMonthly>
        </ButtonsContainer>
      )}
      <YourPlanBox disabled={!isActive}>
        {getPromotionalOffer(selectedPlan?.packageName ?? '') ? (
          // Promotional layout: plan name + tag + original price on first row, offer text + current price on second row
          <VerticalWrapper>
            <HorizontalWrapper alignEnd>
              <HorizontalWrapper>
                <VerticalWrapper>
                  <Text
                    color={getPlanColor(selectedPlan?.packageName)}
                    size={10}
                    bold
                    textAlign="left"
                  >
                    {selectedPlan?.packageName
                      ? splitPlanName(getPlanName(selectedPlan?.packageName)).brand.toUpperCase()
                      : planText?.toUpperCase()}
                  </Text>
                  <Text
                    size={18}
                    textAlign="left"
                    bold
                    color={getPlanColor(selectedPlan?.packageName)}
                  >
                    {splitPlanName(getPlanName(selectedPlan?.packageName)).type.toUpperCase()}
                  </Text>
                </VerticalWrapper>
                <SaveTag>{getPromotionalOffer(selectedPlan?.packageName ?? '')?.saveTag}</SaveTag>
              </HorizontalWrapper>
              <HorizontalWrapper>
                <OriginalPrice textAlign="end">
                  $
                  {formatFloatWithCommasAndDecimals(
                    getPromotionalOffer(selectedPlan?.packageName ?? '')?.originalPrice ?? 0
                  )}
                </OriginalPrice>
              </HorizontalWrapper>
            </HorizontalWrapper>
            <HorizontalWrapper marginTop="10px">
              <OfferText textAlign="left">
                {getPromotionalOffer(selectedPlan?.packageName ?? '')?.offerText}
              </OfferText>
              <Text size={18} textAlign="end">
                {selectedPlan?.price === 0
                  ? 'Free'
                  : `$${formatFloatWithCommasAndDecimals(selectedPlan?.price ?? 0)}`}
              </Text>
            </HorizontalWrapper>
          </VerticalWrapper>
        ) : (
          // Regular layout for non-promotional plans
          <HorizontalWrapper>
            <VerticalWrapper>
              <Text color={getPlanColor(selectedPlan?.packageName)} size={10} bold textAlign="left">
                {selectedPlan?.packageName
                  ? splitPlanName(getPlanName(selectedPlan?.packageName)).brand.toUpperCase()
                  : planText?.toUpperCase()}
              </Text>
              <Text size={18} textAlign="left" bold color={getPlanColor(selectedPlan?.packageName)}>
                {splitPlanName(getPlanName(selectedPlan?.packageName)).type.toUpperCase()}
              </Text>
            </VerticalWrapper>
            {selectedBilledOption === TRANSLATION_TEXT.billed_annually ||
            selectedBilledOption === TRANSLATION_TEXT.one_year ? (
              <VerticalWrapper style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                {selectedPlan?.packageName === 'Rapaport Plus' ||
                selectedPlan?.packageName === 'Rapaport Basic' ? (
                  <Text size={18} textAlign="end">
                    {`$${formatFloatWithCommasAndDecimals(
                      Math.round((selectedPlan?.price ?? 0) / 52)
                    )}/week`}
                  </Text>
                ) : (
                  <Text size={18} textAlign="end">
                    {`$${formatFloatWithCommasAndDecimals(
                      ((selectedPlan?.price ?? 0) / 12).toFixed(2)
                    )}/mo x12`}
                  </Text>
                )}
                <Text textAlign="end">
                  {`$${formatFloatWithCommasAndDecimals(selectedPlan?.price || 0)}/yr`}
                </Text>
              </VerticalWrapper>
            ) : (
              <div>
                <HorizontalWrapper>
                  <Text size={18} textAlign="end">
                    ${formatFloatWithCommasAndDecimals(selectedPlan?.price ?? 0)}
                  </Text>
                  <Text>/{TRANSLATION_TEXT.month}</Text>
                </HorizontalWrapper>
                <Text textAlign="end">
                  ${formatFloatWithCommasAndDecimals((selectedPlan?.price ?? 0) * 12)}/
                  {TRANSLATION_TEXT.year}
                </Text>
              </div>
            )}
          </HorizontalWrapper>
        )}
        {(isUsaAccount || selectedPlan?.packageName?.includes('Primary Supplier')) && (
          <Text size={12} textAlign="start">
            {TRANSLATION_TEXT.includes_rapaport_magazine}
          </Text>
        )}
        <Line />
        <Text textAlign="center" padding="2px">
          {TRANSLATION_TEXT[getPlanText(selectedPlan?.packageName ?? '') ?? 'empty_text']}
        </Text>
      </YourPlanBox>

      {/* <OtherPlansLink
        disabled={!isActive || !countries.some(country => country === form.values.companyCountry)}
        onClick={() => handleOtherAvailablePlansSelected()}
      >
        {TRANSLATION_TEXT.other_available_plans}
        {isActive && !countries.some(country => country === form.values.companyCountry) && (
          <Tooltip
            black
            tooltip={
              TRANSLATION_TEXT.please_select_your_country_first_to_view_other_available_plans
            }
          >
            <OtherPlansLinkTooltipIcon name="info" />
          </Tooltip>
        )}
      </OtherPlansLink> */}
      {!hideAnnuallyYearlyButtons && (
        <AddonsSection
          availableAddons={availableAddons}
          selectedAddons={selectedAddons}
          setSelectedAddons={setSelectedAddon}
          isActive={isActive}
          isAddonSelected={isAddonSelected}
          findAndSelectAddon={findAndSelectAddon}
          findAndUnselectAddon={findAndUnselectAddon}
          getAddonName={getAddonName}
          getAddonText={getAddonText}
          selectedBilledOption={selectedBilledOption}
          numOfAdditionalUser={numOfAdditionalUser}
          setNumber={setNumber}
          errorAdditionalUser={errorAdditionalUser}
          TRANSLATION_TEXT={TRANSLATION_TEXT}
        />
      )}
    </Container>
  );
};
